import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Info } from 'lucide-react';

const SemanticColors = () => {
  const colorPairs = [
    { name: 'background', desc: 'Page background', fg: 'foreground', fgDesc: 'Main text color' },
    { name: 'card', desc: 'Card backgrounds', fg: 'card-foreground', fgDesc: 'Card text' },
    { name: 'primary', desc: 'Primary actions', fg: 'primary-foreground', fgDesc: 'Text on primary' },
    { name: 'secondary', desc: 'Secondary elements', fg: 'secondary-foreground', fgDesc: 'Text on secondary' },
    { name: 'muted', desc: 'Subtle backgrounds', fg: 'muted-foreground', fgDesc: 'Subtle text' },
    { name: 'accent', desc: 'Highlights', fg: 'accent-foreground', fgDesc: 'Text on accent' },
    { name: 'destructive', desc: 'Error/danger', fg: 'destructive-foreground', fgDesc: 'Text on destructive' },
  ];

  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Semantic Colors</h1>
          <p className="text-lg text-muted-foreground">
            UIKit uses semantic color names that adapt to themes and modes automatically.
          </p>
        </div>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            Semantic colors have <strong>meaning</strong> (like "primary" or "destructive") rather than specific values (like "blue" or "red"). This makes your UI consistent across themes.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Core Color Pairs</h2>
          <p className="text-muted-foreground">
            Each background color has a matching foreground color for text:
          </p>

          <div className="space-y-3">
            {colorPairs.map(({ name, desc, fg, fgDesc }) => (
              <div key={name} className="grid grid-cols-2 gap-3">
                <div className={`p-4 rounded-lg bg-${name} text-${name}-foreground`}>
                  <div className="font-mono text-sm">{name}</div>
                  <div className="text-sm opacity-80">{desc}</div>
                </div>
                <div className={`p-4 rounded-lg border bg-${name} text-${name}-foreground`}>
                  <div className="font-mono text-sm">{fg}</div>
                  <div className="text-sm opacity-80">{fgDesc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Live Color Preview</h2>

          <div className="grid gap-3">
            <div className="p-4 rounded-lg bg-background text-foreground border">
              <span className="font-mono text-sm">bg-background text-foreground</span>
            </div>
            <div className="p-4 rounded-lg bg-primary text-primary-foreground">
              <span className="font-mono text-sm">bg-primary text-primary-foreground</span>
            </div>
            <div className="p-4 rounded-lg bg-secondary text-secondary-foreground">
              <span className="font-mono text-sm">bg-secondary text-secondary-foreground</span>
            </div>
            <div className="p-4 rounded-lg bg-muted text-muted-foreground">
              <span className="font-mono text-sm">bg-muted text-muted-foreground</span>
            </div>
            <div className="p-4 rounded-lg bg-accent text-accent-foreground">
              <span className="font-mono text-sm">bg-accent text-accent-foreground</span>
            </div>
            <div className="p-4 rounded-lg bg-destructive text-destructive-foreground">
              <span className="font-mono text-sm">bg-destructive text-destructive-foreground</span>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Using Colors in Code</h2>

          <CodePreview
            title="Tailwind Classes"
            code={`// Background colors
<div className="bg-background">Page background</div>
<div className="bg-card">Card background</div>
<div className="bg-primary">Primary color</div>
<div className="bg-muted">Muted background</div>

// Text colors
<p className="text-foreground">Main text</p>
<p className="text-muted-foreground">Subtle text</p>
<p className="text-primary">Primary colored text</p>

// Borders
<div className="border border-border">Default border</div>
<div className="border border-input">Input border</div>

// Combining
<div className="bg-card text-card-foreground border border-border">
  A card with proper colors
</div>`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Utility Colors</h2>
          <p className="text-muted-foreground">
            Additional colors for specific use cases:
          </p>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Color</th>
                  <th className="text-left p-3 font-semibold">Usage</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">border</td>
                  <td className="p-3 text-muted-foreground">Default borders</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">input</td>
                  <td className="p-3 text-muted-foreground">Form input borders</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">ring</td>
                  <td className="p-3 text-muted-foreground">Focus rings</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">popover</td>
                  <td className="p-3 text-muted-foreground">Popup backgrounds</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">OKLCH Color Science</h2>
          <p className="text-muted-foreground">
            UIKit uses OKLCH color space for perceptually uniform colors:
          </p>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="font-mono text-sm bg-muted p-3 rounded">
                --primary: oklch(0.6 0.2 250);
                <br />
                /* Lightness: 0.6, Chroma: 0.2, Hue: 250° */
              </div>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong>Lightness (L)</strong>: 0-1, controls brightness</li>
                <li>• <strong>Chroma (C)</strong>: 0-0.4, controls saturation</li>
                <li>• <strong>Hue (H)</strong>: 0-360°, the color angle</li>
              </ul>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Why Semantic Colors?</h2>

          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">1.</span>
              <div>
                <strong className="text-foreground">Theme Switching</strong> - Change themes without updating component code
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">2.</span>
              <div>
                <strong className="text-foreground">Dark Mode</strong> - Colors automatically adjust for light/dark
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">3.</span>
              <div>
                <strong className="text-foreground">Consistency</strong> - All components use the same color vocabulary
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">4.</span>
              <div>
                <strong className="text-foreground">Accessibility</strong> - Foreground colors are designed for contrast
              </div>
            </li>
          </ul>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default SemanticColors;
